import { Injectable } from '@angular/core';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';

type JsBarcodeFn = (
  elemento: Element,
  dato: string,
  opciones?: Record<string, unknown>,
) => void;

/**
 * Genera e imprime una etiqueta con el código del producto en formato Code128.
 *
 * Se usa Code128 (y no EAN-13) porque el código del producto puede ser un SKU
 * autogenerado con letras, guiones o guiones bajos, no solo dígitos.
 *
 * `jsbarcode` se carga de forma diferida: solo se descarga al imprimir la primera
 * etiqueta y no se evalúa durante el renderizado en servidor (SSR).
 */
@Injectable({ providedIn: 'root' })
export class EtiquetaProductoService {
  /** Abre la ventana de impresión con la etiqueta. Devuelve `false` si no se pudo generar. */
  async imprimir(producto: IProductoData): Promise<boolean> {
    if (typeof window === 'undefined') return false;

    const codigo = (producto.codigo ?? '').trim();
    if (!codigo) return false;

    // La ventana se abre antes del `await` para que el navegador no la bloquee.
    const ventana = window.open('', '_blank', 'width=420,height=340');
    if (!ventana) return false;

    ventana.document.write(
      '<!doctype html><html><body style="font-family:system-ui;padding:12px">Generando etiqueta...</body></html>',
    );
    ventana.document.close();

    const imagen = await this.generarImagen(codigo);
    if (!imagen) {
      ventana.close();
      return false;
    }

    const nombre = this.escaparHtml(producto.nombre ?? '');
    const precio = Number(producto.precio_venta ?? 0).toFixed(2);

    ventana.document.open();
    ventana.document.write(`<!doctype html>
<html lang="es">
  <head>
    <meta charset="utf-8" />
    <title>Etiqueta ${this.escaparHtml(codigo)}</title>
    <style>
      @page { size: 50mm 30mm; margin: 2mm; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
        color: #0f172a;
      }
      .etiqueta {
        width: 46mm;
        padding: 1mm;
        text-align: center;
      }
      .nombre {
        font-size: 9px;
        font-weight: 700;
        line-height: 1.1;
        max-height: 20px;
        overflow: hidden;
      }
      .precio { font-size: 10px; font-weight: 700; margin-top: 1mm; }
      img { width: 100%; height: auto; display: block; margin-top: 1mm; }
      .no-imprimir { margin-top: 6mm; text-align: center; }
      @media print { .no-imprimir { display: none; } }
    </style>
  </head>
  <body>
    <div class="etiqueta">
      <div class="nombre">${nombre}</div>
      <img src="${imagen}" alt="Código ${this.escaparHtml(codigo)}" />
      <div class="precio">S/ ${precio}</div>
    </div>
    <div class="no-imprimir">
      <button onclick="window.print()">Imprimir</button>
    </div>
  </body>
</html>`);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => ventana.print(), 350);

    return true;
  }

  /** Devuelve el código como PNG en base64, o `null` si no es válido para Code128. */
  async generarImagen(codigo: string): Promise<string | null> {
    if (typeof document === 'undefined') return null;

    const canvas = document.createElement('canvas');

    try {
      const modulo = (await import('jsbarcode')) as unknown as JsBarcodeFn & {
        default?: JsBarcodeFn;
      };
      const JsBarcode = modulo.default ?? modulo;

      JsBarcode(canvas, codigo, {
        format: 'CODE128',
        displayValue: true,
        width: 2,
        height: 55,
        margin: 0,
        fontSize: 14,
        textMargin: 1,
      });

      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }

  private escaparHtml(valor: string): string {
    return valor
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
